import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BsFillEyeFill, BsFillEyeSlashFill } from 'react-icons/bs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import mercadopagoService from '../../services/mercadopago.service';

export default function MercadoPagoConfig() {
  const [accessToken, setAccessToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);
  const [mpConfig, setMpConfig] = useState({ webhookUrl: '', developersUrl: '', site: 'UY' });

  useEffect(() => {
    fetchAccessToken();
    fetchWebhookConfig();
    fetchWebhookSecret();
    fetchMpConfig();
  }, []);

  const fetchMpConfig = async () => {
    try {
      const config = await mercadopagoService.getConfig();
      setMpConfig(config);
    } catch (ex) {
      console.error(ex);
    }
  };

  const fetchAccessToken = async () => {
    try {
      const data = await mercadopagoService.getAccessToken();
      setAccessToken(data?.token || '');
    } catch (ex) {
      console.error(ex);
      toast.error('No se pudo obtener el access token');
    }
  };

  const fetchWebhookConfig = async () => {
    try {
      const config = await mercadopagoService.getWebhookConfig();
      setWebhookEnabled(config?.enabled || false);
    } catch (ex) {
      console.error(ex);
    }
  };

  const fetchWebhookSecret = async () => {
    try {
      const secret = await mercadopagoService.getWebhookSecret();
      setWebhookSecret(secret || '');
    } catch (ex) {
      console.error(ex);
    }
  };

  const handleSave = async () => {
    if (!accessToken.trim()) {
      toast.error('El campo no puede estar vacío');
      return;
    }
    try {
      setSaving(true);
      const result = await mercadopagoService.setAccessToken(accessToken.trim());
      toast.success(result.message || 'Access token actualizado');
    } catch (ex) {
      console.error(ex);
      toast.error('Error al guardar el access token');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecret = async () => {
    try {
      setSavingSecret(true);
      const result = await mercadopagoService.setWebhookSecret(webhookSecret.trim());
      toast.success(result.message || 'Webhook secret actualizado');
    } catch (ex) {
      console.error(ex);
      toast.error('Error al guardar el webhook secret');
    } finally {
      setSavingSecret(false);
    }
  };

  const handleWebhookToggle = async e => {
    const enabled = e.target.checked;
    try {
      setSavingWebhook(true);
      await mercadopagoService.setWebhookConfig(enabled);
      setWebhookEnabled(enabled);
      toast.success(enabled ? 'Webhooks activados' : 'Webhooks desactivados');
    } catch (ex) {
      console.error(ex);
      toast.error('Error al guardar configuración de webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const webhookUrl = mpConfig.webhookUrl || '(configurá APP_PUBLIC_URL en el servidor)';
  const developersUrl =
    mpConfig.developersUrl || 'https://www.mercadopago.com.uy/developers/panel/app';

  return (
    <div id='mercadopagoconfig'>
      <p className='text-xs text-muted-foreground mb-3'>
        País configurado: <strong>{mpConfig.site || 'UY'}</strong>
        {mpConfig.currencyId ? ` · Moneda: ${mpConfig.currencyId}` : null}
      </p>

      {/* Access token */}
      <div className='flex flex-wrap items-center gap-2'>
        <div className='relative flex-1'>
          <Input
            type={showToken ? 'text' : 'password'}
            placeholder='MercadoPago Access Token'
            value={accessToken}
            onChange={e => setAccessToken(e.target.value)}
            className='pr-10'
          />
          <button
            type='button'
            className='absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground'
            onClick={() => setShowToken(prev => !prev)}
            aria-label='Toggle token visibility'
          >
            {showToken ? (
              <BsFillEyeFill className='h-4 w-4' />
            ) : (
              <BsFillEyeSlashFill className='h-4 w-4' />
            )}
          </button>
        </div>
        <Button onClick={handleSave} disabled={!accessToken.trim() || saving} className='ml-2'>
          {saving ? 'Guardando…' : 'Actualizar'}
        </Button>
      </div>

      {/* Webhook secret */}
      <div className='mt-4'>
        <p className='text-sm font-medium text-foreground mb-2'>Webhook secret (firma HMAC)</p>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='relative flex-1'>
            <Input
              type={showSecret ? 'text' : 'password'}
              placeholder='Secret generado en Mercado Pago → Webhooks'
              value={webhookSecret}
              onChange={e => setWebhookSecret(e.target.value)}
              className='pr-10'
            />
            <button
              type='button'
              className='absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground'
              onClick={() => setShowSecret(prev => !prev)}
              aria-label='Toggle secret visibility'
            >
              {showSecret ? (
                <BsFillEyeFill className='h-4 w-4' />
              ) : (
                <BsFillEyeSlashFill className='h-4 w-4' />
              )}
            </button>
          </div>
          <Button onClick={handleSaveSecret} disabled={savingSecret}>
            {savingSecret ? 'Guardando…' : 'Guardar secret'}
          </Button>
        </div>
      </div>

      {/* Webhook toggle */}
      <div className='mt-4'>
        <label className='flex items-center gap-3 cursor-pointer'>
          <div className='relative'>
            <input
              type='checkbox'
              className='sr-only'
              id='webhook-toggle'
              checked={webhookEnabled}
              onChange={handleWebhookToggle}
              disabled={savingWebhook}
            />
            <div
              className={`h-6 w-11 rounded-full transition-colors ${webhookEnabled ? 'bg-primary' : 'bg-muted'} ${savingWebhook ? 'opacity-50' : ''}`}
            >
              <div
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${webhookEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}
              />
            </div>
          </div>
          <div>
            <span className='text-sm font-medium text-foreground'>
              Activar confirmación por Webhook
            </span>
            <span className='text-xs text-muted-foreground ml-2'>
              (recomendado para producción)
            </span>
          </div>
        </label>

        <p className='text-muted-foreground mt-1 text-xs'>
          {webhookEnabled
            ? 'La reserva se confirma automáticamente cuando MercadoPago notifica el pago, incluso si el usuario cierra el navegador.'
            : 'La reserva se confirma cuando el pago es verificado al regresar de Mercado Pago.'}
        </p>

        {webhookEnabled && (
          <div className='mt-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm'>
            <p className='font-semibold text-foreground mb-2'>
              Configuración requerida en MercadoPago
            </p>
            <p className='mb-2'>
              Registrá la siguiente URL en tu aplicación de Mercado Pago y pegá el secret de firma
              arriba:
            </p>
            <code className='block rounded bg-muted px-3 py-2 font-mono text-xs text-foreground break-all'>
              {webhookUrl}
            </code>
            <hr className='my-3 border-border' />
            <p className='mb-1 font-semibold'>Pasos:</p>
            <ol className='list-decimal list-inside space-y-1 text-sm text-foreground'>
              <li>
                Ingresá a{' '}
                <a
                  href={developersUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='underline text-primary'
                >
                  MercadoPago Developers
                </a>
              </li>
              <li>Seleccioná tu aplicación</li>
              <li>
                Andá a <strong>Webhooks</strong> → <strong>Configurar notificaciones</strong>
              </li>
              <li>
                Pegá la URL de arriba, seleccioná el evento <strong>Pagos</strong> y copiá el secret
              </li>
              <li>Guardá los cambios</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
